import {api} from "@/api/client";

export type SubscriptionStatus =
  | "NONE"
  | "TRIALING"
  | "ACTIVE"
  | "PAUSED"
  | "PAST_DUE"
  | "GRACE"
  | "CANCELED"
  | "EXPIRED"
  | "ADMIN_OVERRIDE";

export type PaymentStatus =
    | "PENDING"
    | "SUCCEEDED"
    | "FAILED"
    | "CANCELED"
    | "REFUNDED";

export type PaymentType = "INITIAL" | "RENEWAL" | "PAYMENT_METHOD_BIND";

export type BillingPeriod =
    | "WEEKLY"
    | "MONTHLY"
    | "QUARTERLY"
    | "YEARLY"
    | "ONE_TIME";

export interface PlanPriceInfo {
  id: string;
  billing_period: BillingPeriod;
  amount_minor: number;
  referral_amount_minor: number;
  leader_amount_minor: number;
  currency: string;
}

export interface PlanInfo {
  id: string;
  slug: string;
  name: string;
    tier?: number;
    trial_days?: number;
  prices: PlanPriceInfo[];
}

export interface PaymentMethodInfo {
    id: string;
    is_default: boolean;
    method_type?: string | null;
    title?: string | null;
    display_label?: string | null;
    brand?: string | null;
    last4?: string | null;
    expires_month?: number | null;
    expires_year?: number | null;
}

export interface SubscriptionInfo {
    id: string;
    plan: PlanInfo;
    plan_price?: PlanPriceInfo | null;
  status: SubscriptionStatus;
    current_period_start?: string | null;
    current_period_end?: string | null;
    trial_end?: string | null;
    grace_ends_at?: string | null;
    cancel_at_period_end?: boolean | null;
    next_charge_at?: string | null;
    pause_started_at?: string | null;
    pause_until?: string | null;
    has_access?: boolean;
}

export interface PaymentHistoryItem {
    id: string;
    amount_minor: number;
    currency: string;
    status: PaymentStatus;
    payment_type: PaymentType;
    created_at: string;
}

export function getSubscription() {
    return api<{ subscription?: SubscriptionInfo | null; trial_eligible: boolean }>(
        "/subscription",
    );
}

export function listPlans() {
  return api<{ plans: PlanInfo[] }>("/subscription/plans");
}

export function listSubscriptionPayments(limit = 5) {
    return api<{ payments: PaymentHistoryItem[]; next_cursor?: string | null }>(
        `/subscription/payments?limit=${limit}`,
    );
}

export interface CheckoutRequest {
  plan_price_id: string;
  customer_email?: string;
  customer_phone?: string;
}

export function createCheckout(input: CheckoutRequest) {
  return api<{ checkout_url: string; payment_id: string }>(
    "/subscription/checkout",
    {
      method: "POST",
      json: input,
    },
  );
}

export function cancelSubscription(reason: string) {
    return api<{ subscription: SubscriptionInfo }>("/subscription/cancel", {
        method: "POST",
        json: {reason},
    });
}

export function pauseSubscription(reason: string) {
    return api<{ subscription: SubscriptionInfo }>("/subscription/pause", {
        method: "POST",
        json: {reason},
    });
}

export function unpauseSubscription() {
    return api<{ subscription: SubscriptionInfo }>("/subscription/unpause", {
        method: "POST",
    });
}

export function resumeSubscription() {
    return api<{ subscription: SubscriptionInfo }>("/subscription/resume", {
        method: "POST",
    });
}

export function listPaymentMethods() {
    return api<{ payment_methods: PaymentMethodInfo[] }>("/billing/payment-methods");
}

export function createPaymentMethodCheckout(returnUrl: string) {
    return api<{ checkout_url: string; payment_id: string }>(
        "/billing/payment-methods/checkout",
        {
            method: "POST",
            json: {return_url: returnUrl},
        },
    );
}

export function deletePaymentMethod(id: string) {
    return api<{ payment_methods: PaymentMethodInfo[] }>(
        `/billing/payment-methods/${id}`,
        {
            method: "DELETE",
        },
    );
}

export function setDefaultPaymentMethod(id: string) {
    return api<{ payment_methods: PaymentMethodInfo[] }>(
        `/billing/payment-methods/${id}/default`,
        {
            method: "POST",
        },
    );
}
